/* eslint-disable no-unused-vars */
import getTime from 'date-fns/getTime'
import addMinutes from 'date-fns/addMinutes'
import format from 'date-fns/format'
import { logger } from '@/helpers/helpers'

const cachePlugin = store => {
  const CACHE_TIME = 360

  /**
   * Set data from API to localStorage
   * @param {object} payload - Object with data
   * @returns {string}
   */
  const writeToStorage = payload => {
    return JSON.stringify(payload)
  }

  /**
   * Returns parsed data from localStorage
   * @param {string} payload - Data from localStorage
   * @returns {object}
   */
  const readFromStorage = payload => {
    return JSON.parse(payload)
  }

  /**
   * Set expire date in milliseconds (Current type plus 10 minutes)
   * @returns {number} Time in milliseconds
   */
  const setExpireDate = (response) => {
    const date = getTime(new Date())
    response['cache_expire'] = parseInt(format(addMinutes(date, CACHE_TIME), 'x'))

    return response
  }

  /**
   * Set current date in milliseconds
   * @returns {number} Time in milliseconds
   */
  const setCurrentDate = () => {
    const date = getTime(new Date())
    return parseInt(format(date, 'x'))
  }

  /**
   * Check check cache for expiration
   * @param {object} response - Time in milliseconds
   * @returns {boolean} - true if expired
   */
  const isExpired = (response) => {
    if (response.cache_expire) {
      return (response.cache_expire - setCurrentDate()) <= 0
    } else {
      return true
    }
  }

  /**
   * Remove outdated cache entry in localStorage
   * @param actionName {string}
   */
  const removeCache = actionName => {
    logger(`[${actionName}] - remove cache entry`)
    localStorage.removeItem(actionName)
  }

  /**
   * Check if cache valid or not
   * @param actionName {string}
   * @param refresh {boolean}
   * @returns {boolean}
   */
  const checkCache = (actionName, refresh) => {
    const data = readFromStorage(localStorage.getItem(actionName))
    if (refresh) {
      logger(`[${actionName}] - refresh flag`)
      return true
    } else if (data === null) {
      logger(`[${actionName}] - data ${data}`)
      return true
    } else {
      logger(`[${actionName}] - check if cache expired`)
      return isExpired(data)
    }
  }

  const cacheKeys = actionName => {
    let existing = localStorage.getItem('cache_keys')

    existing = existing ? existing.split(',') : []

    if (existing.indexOf(actionName) === -1) {
      existing.push(actionName)
      localStorage.setItem('cache_keys', existing.toString())
    }
  }

  /**
   * Wrapper around API calls
   * @param {string} type - Type of request (makecall, cpanel_uapi)
   * @param {string} actionName - Endpoint relative path
   * @param {object} payload - Data that appends to request body
   * @returns {Promise<*>}
   */
  async function request (type, actionName, payload) {
    switch (type) {
      case 'makecall':
        const data = {
          method: actionName
        }
        return store.dispatch(type, { ...data })
      default:
        return store.dispatch(type, { ...payload })
    }
  }

  /**
   * Caching function
   * @param {string} type - Type of request (makecall, cpanel_uapi)
   * @param {string} actionName - Endpoint relative path
   * @param {object} payload - Data that appends to request body
   * @param {boolean} refresh - Flag to forced data refresh
   * @returns {Promise<Object>}
   */
  const cache = async (type = '', actionName = '', payload = {}, refresh = false) => {
    cacheKeys(actionName)
    // Check for cache
    if (checkCache(actionName, refresh)) {
      logger(`[${actionName}] - cache was expired. Get a fresh data`)

      removeCache(actionName)

      // Get a fresh data
      const response = setExpireDate(await request(type, actionName, payload))

      // And store it in a localStorage
      localStorage.setItem(actionName, writeToStorage(response))

      // Return cached data
      return readFromStorage(localStorage.getItem(actionName))
    } else {
      logger(`[${actionName}] - cache is not expired. Return data from localStorage`)
      return readFromStorage(localStorage.getItem(actionName))
    }
  }

  store.cache = cache
}

export default cachePlugin
